import React, { useState, useEffect, useCallback } from 'react';
import {
  HStack, Input, Select, FormControl, FormLabel,
  Box, Text, Badge, Flex, Table, Thead, Tbody, Tr, Th, Td,
  IconButton, SimpleGrid, Button, Divider, VStack,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiClock, FiX } from 'react-icons/fi';
import api from '../api';
import PageShell from './PageShell';

const thisMonthFrom = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const today = () => new Date().toISOString().split('T')[0];

export default function TimesheetsDashboard({ isOpen, onClose, fullPage, onBack }) {
  const active = fullPage || isOpen;

  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState({ totalMinutes: 0, totalHours: '0.0', byUser: {}, byProject: {}, count: 0 });
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [filterUser, setFilterUser] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterContact, setFilterContact] = useState('');
  const [filterFrom, setFilterFrom] = useState(thisMonthFrom());
  const [filterTo, setFilterTo] = useState(today());

  const [page, setPage] = useState(0);
  const perPage = 50;

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterUser) params.set('user_id', filterUser);
      if (filterProject) params.set('project_id', filterProject);
      if (filterCompany) params.set('company_id', filterCompany);
      if (filterContact) params.set('contact_id', filterContact);
      if (filterFrom) params.set('from', filterFrom);
      if (filterTo) params.set('to', filterTo);
      const { data } = await api.get(`/work-sessions/billing?${params}`);
      const rows = data.sessions || [];
      setSessions(rows);
      // Build by-project summary
      const byProject = {};
      rows.forEach(r => {
        const name = r.project_name || 'Unassigned';
        byProject[name] = (byProject[name] || 0) + (r.duration_minutes || 0);
      });
      setSummary({ ...data.summary, byProject });
    } catch (e) { console.error(e); }
  }, [filterUser, filterProject, filterCompany, filterContact, filterFrom, filterTo]);

  const fetchMeta = async () => {
    try {
      const [uRes, pRes, coRes, ctRes] = await Promise.all([
        api.get('/calendar/users').catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/companies/search?q=&limit=200').catch(() => ({ data: [] })),
        api.get('/contacts/search?q=&limit=200').catch(() => ({ data: [] })),
      ]);
      setUsers(uRes.data || []);
      // Deduplicate projects by id
      const projs = Array.isArray(pRes.data) ? pRes.data : [];
      setProjects(projs.filter(p => !p.deleted_at).sort((a, b) => (a.project_name || '').localeCompare(b.project_name || '')));
      setCompanies(Array.isArray(coRes.data) ? coRes.data : []);
      setContacts(Array.isArray(ctRes.data) ? ctRes.data : []);
    } catch (e) {}
  };

  useEffect(() => {
    if (active) { fetchData(); fetchMeta(); setPage(0); }
  }, [active, fetchData]); // eslint-disable-line

  const resetFilters = () => {
    setFilterUser(''); setFilterProject(''); setFilterCompany(''); setFilterContact('');
    setFilterFrom(thisMonthFrom()); setFilterTo(today()); setPage(0);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';
  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
  const fmtDuration = (mins) => {
    if (!mins) return '—';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m > 0 ? `${m}m` : ''}`.trim();
  };
  const totalHoursDecimal = (mins) => mins ? (mins / 60).toFixed(1) : '0.0';

  const paged = sessions.slice(page * perPage, (page + 1) * perPage);
  const totalPages = Math.ceil(sessions.length / perPage);
  const sum = summary || {};

  const hasFilters = filterUser || filterProject || filterCompany || filterContact;

  return (
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose} title="Timesheets">

      {/* Summary Cards */}
      <SimpleGrid columns={4} spacing={3} mb={4}>
        <Box bg="blue.50" p={3} borderRadius="md" textAlign="center" border="1px" borderColor="blue.100">
          <Text fontSize="24px" fontWeight="800" color="blue.700" fontFamily="mono">{sum.totalHours || '0.0'}h</Text>
          <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wide">Total Hours</Text>
        </Box>
        <Box bg="green.50" p={3} borderRadius="md" textAlign="center" border="1px" borderColor="green.100">
          <Text fontSize="24px" fontWeight="800" color="green.700">{sum.count || 0}</Text>
          <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wide">Sessions</Text>
        </Box>
        <Box bg="purple.50" p={3} borderRadius="md" textAlign="center" border="1px" borderColor="purple.100">
          <Text fontSize="24px" fontWeight="800" color="purple.700">{Object.keys(sum.byUser || {}).length}</Text>
          <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wide">Users</Text>
        </Box>
        <Box bg="orange.50" p={3} borderRadius="md" textAlign="center" border="1px" borderColor="orange.100">
          <Text fontSize="24px" fontWeight="800" color="orange.700">{Object.keys(sum.byProject || {}).length}</Text>
          <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wide">Projects</Text>
        </Box>
      </SimpleGrid>

      {/* Breakdown: Hours by User + by Project side-by-side */}
      {(Object.keys(sum.byUser || {}).length > 0 || Object.keys(sum.byProject || {}).length > 0) && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={4}>
          {Object.keys(sum.byUser || {}).length > 0 && (
            <Box bg="gray.50" p={3} borderRadius="md" border="1px" borderColor="gray.100">
              <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={2}>Hours by User</Text>
              <VStack spacing={1} align="stretch">
                {Object.entries(sum.byUser).sort((a, b) => b[1] - a[1]).map(([name, mins]) => {
                  const pct = sum.totalMinutes ? Math.round((mins / sum.totalMinutes) * 100) : 0;
                  return (
                    <Box key={name}>
                      <Flex justify="space-between" fontSize="11px" mb={0.5}>
                        <Text fontWeight="600">{name}</Text>
                        <HStack spacing={2}>
                          <Text color="gray.500">{pct}%</Text>
                          <Text fontWeight="700" color="blue.600">{totalHoursDecimal(mins)}h</Text>
                        </HStack>
                      </Flex>
                      <Box bg="blue.100" borderRadius="full" h="4px" w="100%">
                        <Box bg="blue.500" borderRadius="full" h="4px" w={`${pct}%`} />
                      </Box>
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          )}
          {Object.keys(sum.byProject || {}).length > 0 && (
            <Box bg="gray.50" p={3} borderRadius="md" border="1px" borderColor="gray.100">
              <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={2}>Hours by Project</Text>
              <VStack spacing={1} align="stretch">
                {Object.entries(sum.byProject).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name, mins]) => {
                  const pct = sum.totalMinutes ? Math.round((mins / sum.totalMinutes) * 100) : 0;
                  return (
                    <Box key={name}>
                      <Flex justify="space-between" fontSize="11px" mb={0.5}>
                        <Text fontWeight="600" isTruncated maxW="200px">{name}</Text>
                        <HStack spacing={2}>
                          <Text color="gray.500">{pct}%</Text>
                          <Text fontWeight="700" color="green.600">{totalHoursDecimal(mins)}h</Text>
                        </HStack>
                      </Flex>
                      <Box bg="green.100" borderRadius="full" h="4px" w="100%">
                        <Box bg="green.500" borderRadius="full" h="4px" w={`${pct}%`} />
                      </Box>
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          )}
        </SimpleGrid>
      )}

      <Divider mb={3} />

      {/* Filters */}
      <Box bg="gray.50" p={3} borderRadius="md" mb={3} border="1px" borderColor="gray.100">
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase">Filters</Text>
          {hasFilters && (
            <Button size="xs" variant="ghost" leftIcon={<FiX size={10} />} onClick={resetFilters} color="gray.500">
              Reset
            </Button>
          )}
        </Flex>
        <HStack spacing={2} flexWrap="wrap">
          <FormControl w="150px">
            <FormLabel fontSize="xs" mb={0.5}>User</FormLabel>
            <Select size="sm" value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(0); }}>
              <option value="">All Users</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
            </Select>
          </FormControl>
          <FormControl w="180px">
            <FormLabel fontSize="xs" mb={0.5}>Project</FormLabel>
            <Select size="sm" value={filterProject} onChange={e => { setFilterProject(e.target.value); setPage(0); }}>
              <option value="">All Projects</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_name || '(untitled)'}</option>)}
            </Select>
          </FormControl>
          <FormControl w="160px">
            <FormLabel fontSize="xs" mb={0.5}>Company</FormLabel>
            <Select size="sm" value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setPage(0); }}>
              <option value="">All Companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormControl>
          <FormControl w="160px">
            <FormLabel fontSize="xs" mb={0.5}>Contact</FormLabel>
            <Select size="sm" value={filterContact} onChange={e => { setFilterContact(e.target.value); setPage(0); }}>
              <option value="">All Contacts</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''}</option>)}
            </Select>
          </FormControl>
          <FormControl w="135px">
            <FormLabel fontSize="xs" mb={0.5}>From</FormLabel>
            <Input size="sm" type="date" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setPage(0); }} />
          </FormControl>
          <FormControl w="135px">
            <FormLabel fontSize="xs" mb={0.5}>To</FormLabel>
            <Input size="sm" type="date" value={filterTo} onChange={e => { setFilterTo(e.target.value); setPage(0); }} />
          </FormControl>
        </HStack>
      </Box>

      {/* Ledger Table */}
      <Box fontSize="10px" color="gray.500" mb={1}>{sessions.length} session{sessions.length !== 1 ? 's' : ''} • {sum.totalHours || '0.0'} hours total</Box>
      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th px={2} fontSize="9px">Date</Th>
              <Th px={2} fontSize="9px">User</Th>
              <Th px={2} fontSize="9px">Project</Th>
              <Th px={2} fontSize="9px">Task</Th>
              <Th px={2} fontSize="9px">Sub-Task</Th>
              <Th px={2} fontSize="9px">Contact</Th>
              <Th px={2} fontSize="9px">Notes</Th>
              <Th px={2} fontSize="9px">Start</Th>
              <Th px={2} fontSize="9px">End</Th>
              <Th px={2} fontSize="9px" isNumeric>Duration</Th>
              <Th px={2} fontSize="9px">Participants</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paged.length === 0 && (
              <Tr><Td colSpan={11} textAlign="center" py={8} color="gray.400" fontSize="12px">
                No sessions found for the selected filters.
              </Td></Tr>
            )}
            {paged.map((s, idx) => (
              <Tr key={s.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'blue.50' }}>
                <Td px={2} whiteSpace="nowrap" fontSize="11px">{fmtDate(s.start_time)}</Td>
                <Td px={2} fontSize="11px" fontWeight="600">{s.user_name || '—'}</Td>
                <Td px={2} fontSize="11px" maxW="130px" isTruncated>{s.project_name || '—'}</Td>
                <Td px={2} fontSize="11px" maxW="120px" isTruncated>{s.task_title || '—'}</Td>
                <Td px={2} fontSize="10px" maxW="100px" isTruncated color="purple.600">{s.sub_task_title || '—'}</Td>
                <Td px={2} fontSize="10px">{s.contact_first_name ? `${s.contact_first_name} ${s.contact_last_name || ''}` : '—'}</Td>
                <Td px={2} fontSize="10px" maxW="150px" isTruncated color="gray.600">{s.title || '—'}</Td>
                <Td px={2} fontSize="10px" whiteSpace="nowrap">{fmtTime(s.start_time)}</Td>
                <Td px={2} fontSize="10px" whiteSpace="nowrap" color="gray.500">{s.end_time ? fmtTime(s.end_time) : '—'}</Td>
                <Td px={2} fontSize="12px" fontWeight="800" color="blue.600" isNumeric fontFamily="mono">{fmtDuration(s.duration_minutes)}</Td>
                <Td px={2}>
                  <Flex gap={1} flexWrap="wrap">
                    {(s.participants || []).map(p => (
                      <Badge key={p.id} fontSize="8px" colorScheme={p.user_id ? 'purple' : 'orange'}>
                        {p.user_name || (p.first_name ? `${p.first_name} ${p.last_name || ''}` : '?')}
                      </Badge>
                    ))}
                    {(s.participants || []).length === 0 && <Text fontSize="10px" color="gray.300">—</Text>}
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex justify="center" mt={3} gap={1} align="center">
          <IconButton icon={<FiChevronLeft />} size="xs" isDisabled={page <= 0} onClick={() => setPage(p => p - 1)} aria-label="Prev" />
          <Text fontSize="11px" px={2}>Page {page + 1} of {totalPages}</Text>
          <IconButton icon={<FiChevronRight />} size="xs" isDisabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} aria-label="Next" />
        </Flex>
      )}
    </PageShell>
  );
}
